import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, Breakpoint, Button, CircularProgress, Grid, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckBox from '../icons/CheckBox.svg';
import UnCheckedBox from '../icons/UnCheckedBox.svg';
import { InputField } from './InputController';
import CancelIcon from '../icons/cancelIcon.svg';

interface TermsDialogProps {
  open: boolean;
  handleClose?: () => void;
  handleConfirmConsent: () => void;
  handleCheckBoxClicked: () => void;
  checkboxTicked: boolean;
  signatureFullName: string;
  handleChangeSignature: (fullName: string) => void;
  title: string;
  text: string;
  scroll: boolean;
  buttonText: string;
  acceptText?: string;
  maxWidth?: Breakpoint;
}

export const useStyle = makeStyles(() => ({
  text: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: 400,
    fontSize: '18px !important',
    lineHeight: '21px !important',
    color: '#000000 !important',
    overflow: 'auto !important',
  },
  accpetText: {
    fontFamily: 'Work Sans !important',
    fontStyle: 'normal !important',
    fontWeight: 400,
    fontSize: '16px !important',
    lineHeight: '22px !important',
    color: '#000000 !important',
    cursor: 'pointer !important',
  },
  buttontext: {
    fontFamily: 'sans-serif !important',
    fontStyle: 'normal !important',
    fontWeight: 400,
    textTransform: 'capitalize',
    fontSize: '16px !important',
    lineHeight: '22px !important',
  },
  titleText: {},
}));

function TermsDialogWithConsent(props: TermsDialogProps) {
  const classes = useStyle();
  const descriptionElementRef = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    if (props.open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [props.open]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); //using material ui media query

  return (
    <div>
      <Dialog
        open={props.open}
        scroll={props.scroll ? 'paper' : 'body'}
        maxWidth={props.maxWidth || 'md'}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        PaperProps={{
          style: {
            pointerEvents: 'auto',
            borderRadius: isMobile ? '' : '24px',
            height: isMobile ? '100vh' : '60%',
          },
        }}
        onClose={() => {
          props.handleClose && props.handleClose();
        }}
        fullScreen={isMobile}
      >
        <DialogTitle id="scroll-dialog-title">
          <Grid container width="100%">
            <Grid item xs={1}></Grid>
            <Grid item xs={10} sx={{ fontWeight: 700, fontFamily: 'Work Sans !important', fontStyle: 'normal !important', display: 'flex', justifyContent: 'center' }}>
              {props.title}
            </Grid>
            <Grid item xs={1}>
              <IconButton onClick={() => props.handleClose && props.handleClose()}>
                <img src={CancelIcon} alt="" width={24} height={24} />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent
          sx={{
            mx: 2,
            px: 1,
            mb: 2,
            '&::-webkit-scrollbar': {
              width: 7,
            },
            '&::-webkit-scrollbar-track': {
              boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
              borderRadius: '24px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'darkgrey',
              borderRadius: '24px',
            },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box id="scroll-dialog-description" ref={descriptionElementRef} tabIndex={-1} className={classes.text}>
            {props.text ? (
              <div dangerouslySetInnerHTML={{ __html: props.text.replace(/<\/?html>|<\/?body>/g, '') }} />
            ) : (
              <Grid item sx={{ display: 'flex', justifyContent: 'center' }} xs={12}>
                <CircularProgress size={26} />
              </Grid>
            )}
          </Box>
          <Box mt={2}>
            <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', width: '100%' }}>
              {!!props.text && !!props.acceptText && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <img src={props.checkboxTicked ? CheckBox : UnCheckedBox} style={{ width: '15px', height: '15px', cursor: 'pointer', marginRight: 10 }} alt="Checkmark" onClick={props.handleCheckBoxClicked} />
                  <Typography className={classes.accpetText} onClick={props.handleCheckBoxClicked}>
                    {props.acceptText}
                  </Typography>
                </Box>
              )}
            </Box>
            <Grid container sx={{ display: 'flex', alignItems: 'center', width: '100%', marginTop: 2 }}>
              <Grid item xs={12} sm={3} mb={{ xs: 2, sm: 0 }}>
                <Typography className={classes.accpetText}>Enter your full name:</Typography>
              </Grid>
              <Grid item xs={12} sm={6} mb={{ xs: 2, sm: 0 }}>
                <InputField name={'full_name'} placeholder={'First & Last Name'} onChange={(e) => props.handleChangeSignature && props.handleChangeSignature(e.target.value)} value={props.signatureFullName} isError={false} isfullWidth={true} noMargin={true} />
              </Grid>
              <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={props.handleConfirmConsent} sx={{ borderRadius: '20px', fontSize: 'small', width: isMobile ? '100%' : '50%', backgroundColor: '#39A2DB' }} size="large" disabled={!!props.acceptText && (!props.checkboxTicked || !props.signatureFullName)}>
                  <Typography className={classes.buttontext} variant="caption">
                    {props.buttonText}
                  </Typography>
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </div>
  );
}

TermsDialogWithConsent.propTypes = {};

export default TermsDialogWithConsent;
